<template>
    <component :is="isLink ? 'router-link' : 'div'" :to="isLink ? to : undefined" class="stat-card"
        :class="['stat-card--' + tone, { 'stat-card--tinted': tinted, 'stat-card--link': isLink }]">
        <span v-if="icon" class="stat-card__icon" aria-hidden="true"><i :class="icon"></i></span>
        <div class="stat-card__body">
            <p class="stat-card__title">{{ title }}</p>
            <template v-if="hasValue">
                <span v-if="loading" class="stat-card__skeleton" role="status" aria-label="Cargando"></span>
                <p v-else class="stat-card__value">{{ displayValue }}</p>
            </template>
            <p v-if="description" class="stat-card__description">{{ description }}</p>
            <slot></slot>
            <span v-if="isLink && actionLabel" class="stat-card__action">
                {{ actionLabel }}<i class="fas fa-arrow-right" aria-hidden="true"></i>
            </span>
            <div v-if="$slots.actions" class="stat-card__actions"><slot name="actions"></slot></div>
        </div>
        <div v-if="image" class="stat-card__media"><img :src="image" alt="" aria-hidden="true"></div>
    </component>
</template>
<script>
/**
 * Tarjeta reutilizable del tablero: un dato (KPI) o un acceso directo.
 *
 *   <!-- acceso directo con imagen y acción -->
 *   <StatCard tone="info" icon="fas fa-shopping-cart" title="Compras" description="Nuevas mercancías"
 *             :image="img" to="/controlPanel/entradas" action-label="Ir a compras" tinted />
 *
 *   <!-- KPI -->
 *   <StatCard tone="success" icon="fas fa-box" title="Piezas vendidas" :value="1155" :loading="loading" />
 *
 * - `to` vuelve toda la tarjeta un enlace (con `action-label` como llamada a la
 *   acción). `disabled` la deja como tarjeta sin enlace (rol sin permiso).
 * - `value` activa la cifra grande: `null` se ve como "—" y `loading` pinta un
 *   esqueleto. Los números se formatean en es-MX.
 * - Slot por defecto: contenido extra. Slot `actions`: botones propios; no
 *   mezclar con `to` (un botón dentro de un enlace no es válido).
 * - `tinted` tiñe el fondo con el tono (accesos directos); sin él, fondo de
 *   superficie. Siempre hairline y barra lateral de 4px, sin sombra, igual que
 *   `NoticeBanner`. El color sale de los tokens `--app-*`, nunca de un hex.
 *
 * Sin clases `text-h*` de Vuetify: main.css les pone la fuente Inter y los
 * iconos de Font Awesome se verían como cuadros.
 */
const STAT_TONES = ['primary', 'info', 'success', 'warning', 'danger', 'neutral'];

export default {
    name: 'StatCard',
    props: {
        title: { type: String, required: true },
        // Cifra grande. `undefined` = la tarjeta no es un KPI; `null` = KPI sin dato.
        value: { type: [Number, String], default: undefined },
        description: { type: String, default: '' },
        // Icono de Font Awesome (`fas fa-cash-register`).
        icon: { type: String, default: '' },
        // URL de la imagen del lado derecho.
        image: { type: String, default: '' },
        tone: { type: String, default: 'primary', validator: (tone) => STAT_TONES.includes(tone) },
        tinted: { type: Boolean, default: false },
        // Ruta del enlace (cadena u objeto de vue-router).
        to: { type: [String, Object], default: '' },
        actionLabel: { type: String, default: '' },
        disabled: { type: Boolean, default: false },
        loading: { type: Boolean, default: false }
    },
    computed: {
        isLink() {
            return !!this.to && !this.disabled;
        },
        hasValue() {
            return this.value !== undefined || this.loading;
        },
        displayValue() {
            if (this.value === null || this.value === '') return '—';
            return typeof this.value === 'number' ? this.value.toLocaleString('es-MX') : this.value;
        }
    }
};
</script>
<style scoped>
.stat-card {
    --tone: var(--app-primary);
    display: flex;
    align-items: center;
    gap: 12px;
    height: 100%;
    padding: 10px 16px;
    border: 1px solid var(--app-line-strong);
    border-left: 4px solid var(--tone);
    border-radius: 6px;
    background: var(--app-surface);
    color: var(--app-text);
    text-decoration: none;
    transition: border-color 150ms, background-color 150ms;
}
.stat-card--info { --tone: var(--app-info); }
.stat-card--success { --tone: var(--app-success); }
.stat-card--warning { --tone: var(--app-warning); }
.stat-card--danger { --tone: var(--app-danger); }
.stat-card--neutral { --tone: var(--app-text-muted); }
@supports (color: color-mix(in srgb, red 50%, blue)) {
    .stat-card {
        border-color: color-mix(in srgb, var(--tone) 28%, var(--app-line-strong));
        border-left-color: var(--tone);
    }
    .stat-card--tinted {
        background: color-mix(in srgb, var(--tone) 10%, var(--app-surface));
    }
    .stat-card--link:hover {
        background: color-mix(in srgb, var(--tone) 16%, var(--app-surface));
        border-color: color-mix(in srgb, var(--tone) 55%, transparent);
    }
}
.stat-card--link {
    cursor: pointer;
}
.stat-card--link:focus-visible {
    outline: 2px solid var(--tone);
    outline-offset: 2px;
}
.stat-card__body {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex: 1 1 auto;
    min-width: 0;
}
.stat-card__icon {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 6px;
    font-size: 17px;
    color: var(--tone);
    background: var(--app-surface-sunken);
}
@supports (color: color-mix(in srgb, red 50%, blue)) {
    .stat-card__icon {
        /* el tono oscurecido con la tinta: el amarillo puro no se ve sobre su propio fondo */
        color: color-mix(in srgb, var(--tone) 70%, var(--v-onsurface-base, #000));
        background: color-mix(in srgb, var(--tone) 16%, var(--app-surface));
    }
}
.stat-card__title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
}
.stat-card__value {
    margin: 2px 0 0;
    font-size: 28px;
    font-weight: 700;
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
}
.stat-card__skeleton {
    display: block;
    width: 96px;
    height: 28px;
    margin-top: 2px;
    border-radius: 4px;
    background: var(--app-hover);
    animation: stat-card-pulse 1.2s ease-in-out infinite;
}
@keyframes stat-card-pulse {
    50% { opacity: .45; }
}
.stat-card__description {
    margin: 2px 0 0;
    font-size: 13px;
    color: var(--app-text-muted);
}
.stat-card__action {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding-top: 6px;
    font-size: 13px;
    font-weight: 600;
    color: var(--app-text);
}
.stat-card__action i {
    font-size: 11px;
    transition: transform 150ms;
}
.stat-card--link:hover .stat-card__action i {
    transform: translateX(3px);
}
.stat-card__actions {
    display: flex;
    gap: 8px;
    padding-top: 6px;
}
.stat-card__media {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    margin-left: auto;
}
.stat-card__media img {
    width: 80px;
    height: 80px;
    object-fit: contain;
}
@media (max-width: 599px) {
    .stat-card__media img {
        width: 64px;
        height: 64px;
    }
}
@media (prefers-reduced-motion: reduce) {
    .stat-card,
    .stat-card__action i {
        transition: none;
    }
    .stat-card__skeleton {
        animation: none;
    }
}
</style>
